/**
 * MUTAGIM.SHOP — Brands Marquee Component
 * Bandeau défilant des marques partenaires
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BrandsMarquee() {
  const { i18n, t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const lang = i18n.language?.split('-')[0] || 'fr';

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        console.log('[BrandsMarquee] Fetching brands from:', `${API_URL}/api/brands-marquee`);
        const res = await fetch(`${API_URL}/api/brands-marquee`);
        console.log('[BrandsMarquee] Response status:', res.status);
        if (res.ok) {
          const result = await res.json();
          console.log('[BrandsMarquee] Data received:', result);
          setData(result);
        } else {
          console.error('[BrandsMarquee] Failed to fetch:', res.status);
        }
      } catch (err) {
        console.error('[BrandsMarquee] Error fetching brands marquee:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  // Prepare data for rendering
  const settings = data?.settings;
  const brands = data?.brands || [];
  const title = settings?.title?.[lang] || settings?.title?.fr || '';
  const duplicatedBrands = brands.length > 0 ? [...brands, ...brands, ...brands] : [];

  // ALWAYS render something for debugging
  return (
    <section 
      className="w-full overflow-hidden py-6"
      style={{ backgroundColor: data?.settings?.background_color || '#000000', minHeight: '80px' }}
      data-testid="brands-marquee"
    >
      {loading ? (
        <p className="text-center text-white py-4">Chargement des marques...</p>
      ) : !data?.enabled ? (
        <p className="text-center text-white py-4">Bandeau désactivé</p>
      ) : !data?.brands?.length ? (
        <p className="text-center text-white py-4">Aucune marque à afficher</p>
      ) : (
        <>
          {/* Texte centré et grand */}
          <div className="flex items-center justify-center mb-8">
            <span 
              className="text-white italic"
              style={{ fontFamily: "'Caveat', 'Comic Sans MS', cursive", fontSize: '42px' }}
            >
              {lang === 'fr' ? 'Cliquez sur votre marque favorite' : lang === 'en' ? 'Click on your favorite brand' : 'לחץ על המותג האהוב עליך'}
            </span>
          </div>
          
          <div className="relative">
            <div 
              className="flex items-center gap-16 animate-marquee"
              style={{
                animationDuration: `${settings?.speed || 30}s`
              }}
            >
              {duplicatedBrands.map((brand, index) => {
                // Handle name that might be object {fr, en, he} or string
                const brandName = typeof brand.name === 'object' 
                  ? (brand.name[lang] || brand.name.fr || brand.name.en || 'Unknown')
                  : brand.name;
                
                return (
                <Link
                  key={`${brand.id}-${index}`}
                  to={`/products?brand=${encodeURIComponent(brandName)}`}
                  className="flex-shrink-0 flex items-center justify-center h-12 px-4 opacity-70 hover:opacity-100 hover:scale-110 transition-all duration-300 cursor-pointer"
                  title={`${lang === 'fr' ? 'Voir les produits' : lang === 'en' ? 'View products' : 'צפה במוצרים'} ${brandName}`}
                >
                  {brand.logo_url && brand.logo_url !== 'None' && brand.logo_url.trim() !== '' ? (
                    <img 
                      src={brand.logo_url.startsWith('/api') ? `${API_URL}${brand.logo_url}` : brand.logo_url} 
                      alt={brandName}
                      className="h-full w-auto object-contain max-w-[120px] filter brightness-0 invert"
                      loading="lazy"
                      onError={(e) => { 
                        e.target.style.display = 'none'; 
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  {/* Logo texte stylisé comme Louis Vuitton */}
                  <div 
                    className="flex items-center justify-center px-3 py-1"
                    style={{ 
                      display: brand.logo_url && brand.logo_url !== 'None' && brand.logo_url.trim() !== '' ? 'none' : 'flex',
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '2px'
                    }}
                  >
                    <span 
                      className="text-white tracking-[0.25em] font-light"
                      style={{ 
                        fontFamily: "'Didot', 'Bodoni MT', 'Times New Roman', serif",
                        fontSize: '14px',
                        letterSpacing: '0.25em',
                        textTransform: 'uppercase',
                        fontWeight: '300'
                      }}
                    >
                      {brandName}
                    </span>
                  </div>
                </Link>
              )})}
            </div>
          </div>
        </>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');
        
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        .animate-marquee {
          animation: marquee linear infinite;
          width: max-content;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 1.5s ease-in-out infinite;
        }
        
        .font-handwriting {
          font-family: 'Caveat', 'Comic Sans MS', cursive;
        }
      `}</style>
    </section>
  );
}

export default BrandsMarquee;
